import { supabase } from "@/integrations/supabase/client";
import { LLMModel } from "@/utils/config/elevenlabs";

const MODELS_STORAGE_KEY = "elevenlabs_llm_models_cache";
const MODELS_STORAGE_EXPIRY = 15 * 60 * 1000; // 15 minutes

interface ModelsCacheEntry {
  models: LLMModel[];
  timestamp: number;
}

export class LlmModelService {
  private static instance: LlmModelService;

  private constructor() {}

  public static getInstance(): LlmModelService {
    if (!LlmModelService.instance) {
      LlmModelService.instance = new LlmModelService();
    }
    return LlmModelService.instance;
  }

  public async getModels(forceRefresh = false): Promise<LLMModel[]> {
    try {
      const { data, error } = await supabase.functions.invoke("elevenlabs", {
        body: {
          resource: "llm-models",
          action: "list",
          params: { forceRefresh },
        },
      });

      if (error) {
        console.warn("Failed to fetch live ElevenLabs model list:", error);
        return this.getCachedModels();
      }

      const payload = data?.data || data;
      const models = Array.isArray(payload?.models) ? this.normalizeModels(payload.models) : [];

      if (models.length === 0) {
        return this.getCachedModels();
      }

      this.cacheModels(models);
      return models;
    } catch (error) {
      console.warn("Failed to load ElevenLabs models:", error);
      return this.getCachedModels();
    }
  }

  private normalizeModels(models: any[]): LLMModel[] {
    return models
      .filter((model) => model && typeof model.id === "string")
      .map(
        (model): LLMModel => ({
          id: model.id,
          name: typeof model.name === "string" ? model.name : model.id,
          provider: model.provider || "ElevenLabs",
        }),
      );
  }

  private cacheModels(models: LLMModel[]): void {
    try {
      const cacheEntry: ModelsCacheEntry = {
        models,
        timestamp: Date.now(),
      };
      localStorage.setItem(MODELS_STORAGE_KEY, JSON.stringify(cacheEntry));
    } catch (error) {
      console.warn("Failed to cache model list:", error);
    }
  }

  private getCachedModels(): LLMModel[] {
    try {
      const cached = localStorage.getItem(MODELS_STORAGE_KEY);
      if (!cached) return [];

      const entry: ModelsCacheEntry = JSON.parse(cached);
      const isExpired = Date.now() - entry.timestamp > MODELS_STORAGE_EXPIRY;
      if (isExpired) {
        localStorage.removeItem(MODELS_STORAGE_KEY);
        return [];
      }

      return Array.isArray(entry.models) ? entry.models : [];
    } catch {
      return [];
    }
  }
}
